import apiRepository from '../../api/api-repository';
import WebStorage from './util/web-storage';

let storeSettings;

const stateKeys = [ 
  'uid', 'type', 'token', 'createdAt', 'seenAt', 'expireAt', 'appCode', 'codCli', 'username',
  'roles', 'props', 'logoutRequested'
];

const getPropsWithPrefix = (props, prefix)=> {
  let o = { };
  Object.keys(props).forEach((x)=> {
    if(x.startsWith(prefix)) o[x.replace(prefix, '')] = props[x];
  });
  return o;
};

const actionInitSession = async (context)=> {
  let session = WebStorage.find(storeSettings.prefix);
  
  if(session != null && !isTokenExpired(session.expireAt)){
    context.commit('setSession', session);
    return new Promise((resolve)=> resolve(session));
  }

  try{
    const res = await apiRepository.session().login();
    session = res.data;
    WebStorage.put(storeSettings.prefix, session);
    context.commit('setSession', session);
    return new Promise((resolve)=> resolve(session));
  }
  catch(e){
    context.commit('setSession', null);
    return new Promise((reject)=> reject(e));
  }
};

const actionInvalidateSession = async (context)=> {
  const res = await apiRepository.session().logout();
  context.commit('setSession', null);
  WebStorage.clear(storeSettings.prefix);
  return new Promise((resolve)=> resolve(res.success));
};

const actionRemoveSession = (context)=> {
  WebStorage.clear(storeSettings.prefix);
  context.commit('setSession', null);
  const event = new CustomEvent('logout', { bubbles: true });
  window.dispatchEvent(event);
  return new Promise((resolve)=> resolve(WebStorage.find(storeSettings.prefix) === null));
};

const actionCheckAccessByRole = (context, route)=> {
  if(isTokenExpired(context.state)){
    return context.dispatch('initSession')
      .then((session)=> new Promise((resolve)=> resolve(hasAccessOnRoute(session.roles, route))))
      .catch((e)=> new Promise((resolve)=> resolve(hasAccessOnRoute((e.role) ? e.role : null, route))));
  }
  else return new Promise((resolve)=> {
    resolve(hasAccessOnRoute(context.state.roles, route));
  });
};

const actionRequestLogout = (context)=> {
  context.commit('broadcastLogout', true);
}

const isTokenExpired = (expireAt)=> {
  return expireAt < new Date().getTime();
}

const hasAccessOnRoute = (roles, route)=> {
  if(roles == null) return false;
  else if(route.meta && route.meta.roles) return route.meta.roles.some((r)=> roles.some((x)=> r === x));
  else return true;
}

const requestLogout = (state, logoutRequested)=> {
  state.logoutRequested = logoutRequested;
  window.dispatchEvent(new CustomEvent('argo:logout', { bubbles: true }));
}

export const vuexArgoCreateStoreModule = (settings)=> {
  storeSettings = {
    namespaced: (settings.namespaced) ? settings.namespaced : false,
    appCode: settings.appCode,
    roleAdmin: settings.roleAdmin, roleReg: settings.roleReg,
    persist: (settings.persist) ? settings.persist : false,
    prefix: (settings.prefix) ? settings.prefix : 'argo'
  };

  const state = {
    uid: null, type: null, token: null, 
    createdAt: null, seenAt: null, expireAt: null,
    appCode: null, codCli: null, username: null, roles: null,
    props: null, logoutRequested: false,
  };

  const getters = {
    isInitialized: (state)=> (state.token != null),
    sessionToken: (state)=> (isTokenExpired(state.expireAt)) ? null : state.token,
    user: (state)=> (state.props != null) ? getPropsWithPrefix(state.props, 'user.') : { },
    customer: (state)=> (state.props != null) ? getPropsWithPrefix(state.props, 'customer.') : { },
    isAdministrator: (state)=> (state.roles != null) ? state.roles.some((r)=> r === storeSettings.roleAdmin) : false,
    isRegister: (state)=> (state.roles != null) ? state.roles.some((r)=> r === storeSettings.roleReg || r === storeSettings.roleAdmin) : false,
    expireAt: (state)=> state.expireAt
  };

  const mutations = {
    setSession: (state, session)=> stateKeys.forEach((k)=> state[k] = (session != null) ? session[k] : null),
    broadcastLogout: requestLogout
  };

  const actions = {
    initSession: actionInitSession,
    invalidateSession: actionInvalidateSession,
    checkAccess: actionCheckAccessByRole,
    removeSession: actionRemoveSession,
    requestLogout: actionRequestLogout,
  };

  return { namespaced: storeSettings.namespaced, state: state, getters: getters, mutations: mutations, actions: actions };
};

export const vuexArgoCreateRouterGuard = (store, vuexNamespace)=> {
  return (to, from, next)=> {
    const checkAction = (vuexNamespace) ? `${vuexNamespace}/checkAccess` : 'checkAccess';
    store.dispatch(checkAction, to).then((hasAccess)=> hasAccess ? next() : next('/error/forbidden'));
  }
};